(() => {
  const INDEX_BY_TYPE = {
    'b2b-retailer': ['all-retailer', 'all'],
    'b2b-cosmetician': ['all-cosmetician', 'all'],
  };
  const PREDICTIVE_LIMIT = 8;
  const PAGE_LIMIT = 12;
  const indexCache = {};
  const cardCache = {};

  function isB2BCustomer(type) {
    return type === 'b2b-retailer' || type === 'b2b-cosmetician';
  }

  function customerType() {
    return document.querySelector('[data-customer-type]')?.dataset.customerType || 'b2c';
  }

  function routesRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/ue/g, 'u')
      .replace(/ae/g, 'a')
      .replace(/oe/g, 'o')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function queryTerms(query) {
    return normalize(query)
      .replace(/(^|\s)tag [a-z0-9-]+/g, ' ')
      .split(/\s+/)
      .filter((term) => term.length >= 2);
  }

  function productMatches(product, terms) {
    if (!terms.length) return false;
    const haystack = normalize(
      [product.title, product.handle, product.vendor, product.type, ...(product.skus || [])].join(' '),
    );
    return terms.every((term) => haystack.includes(term));
  }

  function hasCustomerTag(product, type) {
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    const normalized = tags.map((tag) => String(tag).trim());
    if (type === 'b2b-retailer') return normalized.includes('b2b-retailer');
    if (type === 'b2b-cosmetician') return normalized.includes('b2b-cosmetician');
    return false;
  }

  async function fetchIndexPage(handle, page) {
    const url = new URL(`${routesRoot()}collections/${handle}`, window.location.origin);
    url.searchParams.set('view', 'b2b-search-index');
    if (page > 1) url.searchParams.set('page', String(page));
    const response = await fetch(url.toString());
    if (!response.ok) return { pages: 1, products: [] };
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = doc.querySelector('#b2b-search-index');
    const pages = parseInt(root?.dataset.pages || '1', 10) || 1;
    let products = [];
    try {
      products = JSON.parse(root?.querySelector('script[type="application/json"]')?.textContent || '[]');
    } catch (error) {
      products = [];
    }
    return { pages, products };
  }

  async function loadCollectionIndex(handle) {
    const first = await fetchIndexPage(handle, 1);
    const rest = [];
    for (let page = 2; page <= first.pages; page += 1) {
      rest.push(fetchIndexPage(handle, page));
    }
    const loaded = await Promise.all(rest);
    return [first, ...loaded].flatMap((result) => result.products || []);
  }

  async function loadIndex(type) {
    const handles = INDEX_BY_TYPE[type] || [];
    const cacheKey = handles.join(',');
    if (indexCache[cacheKey]) return indexCache[cacheKey];
    if (indexCache[`${cacheKey}:pending`]) return indexCache[`${cacheKey}:pending`];

    indexCache[`${cacheKey}:pending`] = (async () => {
      const groups = await Promise.all(handles.map((handle) => loadCollectionIndex(handle)));
      const byId = new Map();
      groups.flat().forEach((product) => {
        if (product?.id) byId.set(product.id, product);
      });
      const products = Array.from(byId.values());
      indexCache[cacheKey] = products;
      return products;
    })();

    try {
      return await indexCache[`${cacheKey}:pending`];
    } finally {
      delete indexCache[`${cacheKey}:pending`];
    }
  }

  async function matchingProducts(type, query, limit) {
    const terms = queryTerms(query);
    if (!isB2BCustomer(type) || !terms.length) return [];
    const products = await loadIndex(type);
    return products
      .filter((product) => hasCustomerTag(product, type) && productMatches(product, terms))
      .slice(0, limit);
  }

  async function fetchCard(handle) {
    if (cardCache[handle]) return cardCache[handle].cloneNode(true);
    const url = `${routesRoot()}products/${handle}?view=b2b-search-card`;
    const response = await fetch(url);
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const item = doc.querySelector('[data-product-handle]');
    if (!item) return null;
    cardCache[handle] = item;
    return item.cloneNode(true);
  }

  function existingHandles(root) {
    return new Set(
      Array.from(root.querySelectorAll('[data-product-handle], [data-product]')).map(
        (el) => el.dataset.productHandle || el.dataset.product,
      ),
    );
  }

  async function cardsForQuery(type, query, limit, root) {
    const matches = await matchingProducts(type, query, limit);
    const used = root ? existingHandles(root) : new Set();
    const pending = matches
      .filter((product) => product.handle && !used.has(product.handle))
      .map(async (product) => {
        const card = await fetchCard(product.handle);
        return card ? { handle: product.handle, card } : null;
      });
    const cards = [];
    (await Promise.all(pending)).forEach((result) => {
      if (!result || used.has(result.handle)) return;
      used.add(result.handle);
      cards.push(result.card);
    });
    return cards;
  }

  function preparePredictiveCard(card, index) {
    card.classList.add('predictive-search__list-item', 'collection-product-card');
    card.setAttribute('role', 'option');
    card.id = `predictive-search-option-product-b2b-${card.dataset.productId || index}`;
    return card;
  }

  function ensurePredictiveList(root) {
    let list = root.querySelector('#predictive-search-results-products-list');
    if (list) return list;

    const results = root.querySelector('#predictive-search-results') || root;
    results.querySelector('.predictive-search__text')?.remove();

    let wrapper = results.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.id = 'predictive-search-results-groups-wrapper';
      wrapper.className = 'predictive-search__results-groups-wrapper';
      results.prepend(wrapper);
    }

    let group = wrapper.querySelector('.predictive-search__result-group');
    if (!group) {
      group = document.createElement('div');
      group.className = 'predictive-search__result-group';
      wrapper.prepend(group);
    }

    let tabs = group.querySelector('.predictive-search__result-tabs');
    if (!tabs) {
      tabs = document.createElement('div');
      tabs.className = 'predictive-search__result-tabs';
      group.prepend(tabs);
    }

    if (!tabs.querySelector('[data-type-target="products"]')) {
      const tab = document.createElement('div');
      tab.className = 'predictive-search__result-tab active';
      tab.dataset.typeTarget = 'products';
      const title = document.createElement('span');
      title.className = 'predictive-search__result-tab-title h4';
      title.textContent = window.themeSettings?.searchProductsLabel || 'Products';
      tab.appendChild(title);
      tabs.prepend(tab);
    }

    const listWrap = document.createElement('div');
    list = document.createElement('ul');
    list.id = 'predictive-search-results-products-list';
    list.className = 'predictive-search__results-list predictive-search__results-list_products list-unstyled active';
    list.dataset.type = 'products';
    listWrap.appendChild(list);
    tabs.after(listWrap);
    return list;
  }

  async function mergeIntoMarkup(markup, type, query) {
    if (!isB2BCustomer(type)) return markup;
    const doc = new DOMParser().parseFromString(`<div id="b2b-merge-root">${markup}</div>`, 'text/html');
    const root = doc.querySelector('#b2b-merge-root');
    const cards = await cardsForQuery(type, query, PREDICTIVE_LIMIT, root);
    if (!cards.length) return markup;
    const list = ensurePredictiveList(root);
    cards.forEach((card, index) => list.prepend(preparePredictiveCard(card, index)));
    return root.innerHTML;
  }

  function updateSearchCount(extraCount) {
    const countEl = document.getElementById('SearchResultsCount');
    if (!countEl) return;
    const nativeCount = parseInt(countEl.dataset.visibleCount || '0', 10) || 0;
    const total = nativeCount + extraCount;
    const template = countEl.dataset.resultsText || window.themeSettings?.searchResultsText || '';
    if (total === 0) {
      if (countEl.dataset.noResultsText) countEl.textContent = countEl.dataset.noResultsText;
      return;
    }
    if (template.includes('[count]')) {
      countEl.textContent = template.replace('[count]', String(total));
    }
    document.querySelector('.template-search')?.classList.remove('template-search--empty');
  }

  function revealSearchPage() {
    document.getElementById('product-grid')?.classList.remove('loading');
  }

  async function enrichSearchPage() {
    const grid = document.getElementById('product-grid');
    const container = document.getElementById('ProductGridContainer');
    if (!grid || !container || !document.body.classList.contains('template-search')) return;
    const type = container.dataset.customerType || customerType();
    if (!isB2BCustomer(type)) return;

    const query = new URLSearchParams(window.location.search).get('q') || '';
    let cards = [];
    try {
      cards = await cardsForQuery(type, query, PAGE_LIMIT, grid);
    } catch (error) {
      cards = [];
    }

    let list = grid.querySelector('.collection-product-list');
    if (!cards.length) {
      if (list && !list.querySelector('.collection-product-card')) {
        const heading = list.previousElementSibling;
        if (heading?.classList.contains('search-results__top')) heading.remove();
        list.remove();
      }
      updateSearchCount(0);
      revealSearchPage();
      return;
    }

    if (!list) {
      const heading = document.createElement('div');
      heading.className = 'search-results__top first';
      const title = document.createElement('h2');
      title.className = 'search-results__title h4';
      title.textContent = window.themeSettings?.searchProductsLabel || '';
      heading.appendChild(title);
      list = document.createElement('ul');
      list.className = 'collection-product-list collection-product-list--1-mobile';
      list.setAttribute('role', 'list');
      grid.appendChild(heading);
      grid.appendChild(list);
    }

    cards.forEach((card) => {
      card.classList.add('template-search__item', 'collection-product-card');
      card.removeAttribute('role');
      list.prepend(card);
    });
    updateSearchCount(cards.length);
    revealSearchPage();
    try {
      if (typeof colorSwatches === 'function') colorSwatches();
    } catch (error) {}
  }

  window.MG_B2BSearchExtras = {
    isB2BCustomer,
    mergeIntoMarkup,
    enrichSearchPage,
  };

  const type = customerType();
  if (isB2BCustomer(type)) loadIndex(type).catch(() => {});

  document.addEventListener('DOMContentLoaded', () => {
    enrichSearchPage().catch(() => revealSearchPage());
  });
})();
